/** @jsxImportSource @emotion/react */
import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../../../actions/snackbarActions";
import PageTitle from "../../../Utils/smsTitle";
import { SubHeader } from "../../../../Containers/SubHeader";
import MainSnackbar from "../../../Utils/Snackbar/SmsSnackbar";
import ObGynSidebar from "../../Utils/ObGynSidebar";
import ObGynPatientInfo from "../../Utils/ObGynPatientInfo";
import { useCervicalScreening } from "./hooks/useCervicalScreening";
import { CervicalScreeningForm } from "./CervicalScreeningForm";
import { CervicalScreeningTimeline } from "./CervicalScreeningTimeline";
import {
    CSPageWrapper,
    CSLoading,
    CSProcedureBlocked,
    CSProcedureBlockedTitle,
    CSProcedureBlockedText,
    CSProcedureBlockedLink,
} from "./CervicalScreeningStyles";
import { ObGynTabBar, ObGynTabBtn, ObGynTabBadge } from "../../Utils/ObGynTabStyles";
import { history } from "../../../../Helpers/history";

/* ═══════════════════════════════════════════════════════════════
   CervicalScreeningPage — Main page for Task 19:
   Cervical Cancer Screening
   Tabs: Screening | History
   Route: /gyn-and-obs-cervical-screening/:patient_number/:visit_id/:patient_type
   ═══════════════════════════════════════════════════════════════ */

const CervicalScreeningPage = function (props) {
    const actions = props.actions;
    const snackbar = props.snackbar;
    const params = props.match.params;
    const patient_number = params.patient_number;
    const visit_id = params.visit_id;
    const patient_type = params.patient_type;

    /* ── Hook ── */
    const cs = useCervicalScreening(actions, patient_number, visit_id);

    const closeSnackbar = function () { actions.snackbarActions.hideSnackbar(); };

    /* ── Loading ── */
    if (cs.loading) {
        return (
            <div>
                <PageTitle title="Cervical Cancer Screening" />
                <SubHeader title="OB/GYN" subTitle="Cervical Cancer Screening" />
                <MainSnackbar variant={snackbar.variant} message={snackbar.message}
                    open={snackbar.open} handleCloseBar={closeSnackbar} />
                <ObGynSidebar patient_number={patient_number} visit_id={visit_id} patient_type={patient_type}>
                    <ObGynPatientInfo patient_number={patient_number} visit_id={visit_id} patient_type={patient_type} />
                    <CSPageWrapper>
                        <CSLoading>⏳ Loading cervical screening data...</CSLoading>
                    </CSPageWrapper>
                </ObGynSidebar>
            </div>
        );
    }

    return (
        <div>
            <PageTitle title="Cervical Cancer Screening" />
            <SubHeader title="OB/GYN" subTitle="Cervical Cancer Screening" />
            <MainSnackbar variant={snackbar.variant} message={snackbar.message}
                open={snackbar.open} handleCloseBar={closeSnackbar} />
            <ObGynSidebar patient_number={patient_number} visit_id={visit_id} patient_type={patient_type}>
                <ObGynPatientInfo patient_number={patient_number} visit_id={visit_id} patient_type={patient_type} />
                <CSPageWrapper>

                    {/* ─── Tab Bar ─── */}
                    <ObGynTabBar>
                        <ObGynTabBtn
                            active={cs.activeTab === "screening"}
                            onClick={function () { cs.setActiveTab("screening"); }}
                        >
                            Screening
                        </ObGynTabBtn>
                        <ObGynTabBtn
                            active={cs.activeTab === "history"}
                            onClick={function () { cs.setActiveTab("history"); }}
                        >
                            History
                            <ObGynTabBadge active={cs.activeTab === "history"}>
                                {cs.screenings.length}
                            </ObGynTabBadge>
                        </ObGynTabBtn>
                    </ObGynTabBar>

                    {/* ─── Tab Content ─── */}
                    {cs.activeTab === "screening" && (
                        cs.procedureChecking ? (
                            <CSLoading>Checking eligibility...</CSLoading>
                        ) : !cs.procedureAllowed ? (
                            <CSProcedureBlocked>
                                <CSProcedureBlockedTitle>Procedure Required</CSProcedureBlockedTitle>
                                <CSProcedureBlockedText>
                                    Cervical Cancer Screening requires a procedure request<br/>. 
                                    Please request and pay for the  procedure first.
                                </CSProcedureBlockedText>
                                <button className="btn btn-sm sms-info-btn" onClick={()=>history.push(`/gyn-and-obs-procedure/${patient_number}/${visit_id}/${patient_type}`)}>
                                    Go to Procedures
                                </button>
                             
                            </CSProcedureBlocked>
                        ) : (
                            <CervicalScreeningForm cs={cs} />
                        )
                    )}

                    {cs.activeTab === "history" && (
                        <CervicalScreeningTimeline cs={cs} />
                    )}

                </CSPageWrapper>
            </ObGynSidebar>
        </div>
    );
};

/* ─── Redux Connect ─── */
function mapStateToProps(state) {
    return { snackbar: state.snackbar };
}

function mapDispatchToProps(dispatch) {
    return { actions: { snackbarActions: bindActionCreators(snackbarActions, dispatch) } };
}

export default connect(mapStateToProps, mapDispatchToProps)(CervicalScreeningPage);
